import React from 'react';
import { View } from 'react-native';
import { Props, Post, Paginator, Tag, TagType } from 'shared/@types';
import { useTranslation } from 'react-i18next';
import { apiRoute } from 'shared/library/api-route';
import { H1, H2 } from 'shared/components/title';
import { PostCard } from 'shared/components/post-card';
import { PageBuilder } from 'shared/library/page-builder';
import { Link } from 'shared/components/link';
import { Text } from 'shared/components/text';
import { Image } from 'shared/components/image';

import { PageContent } from 'shared/components/page-content';
import {
  compactPostCardList,
  compactPostCardPaginator,
  compactTagsForBlogTopics,
  compactTagsForLinks,
} from 'shared/library/compact-page-data';
import { useRoute } from 'shared/library/routes/route';
import { getPostImage } from 'shared/library/get-model-image';
import { useFormatter } from 'shared/library/use-formatter';
import { getInternalRoutePath } from 'shared/library/routes/route-registry';
import { platformOS } from 'shared/library/platform';

const BLOG_INDEX_POSTS_PER_PAGE = 24;
const FEATURED_POST_COUNT = 7;
const LATEST_POST_COUNT = 5;
const POPULAR_TOPIC_COUNT = 8;
const LATEST_POST_SORT = 'latest';

interface IndexPostsScreenProps extends Props {
  initialPosts: Paginator<Post>;
  featuredPosts: Post[];
  featuredTags: Tag[];
  topicTags: Tag[];
}

const IndexPostsScreen = ({
  initialPosts,
  featuredPosts = [],
  featuredTags = [],
  topicTags = [],
}: IndexPostsScreenProps) => {
  const { t } = useTranslation('screens');
  const latestInitialPosts = getPostsByLatestPublishedDate(initialPosts.data);
  const fallbackTopicTags = topicTags.length > 0 ? topicTags : getTagsFromPosts(latestInitialPosts);
  const visibleTopicTags =
    featuredTags.length > 0
      ? mergeFeaturedTags(featuredTags, fallbackTopicTags)
      : fallbackTopicTags;
  const popularTopicTags = getPopularTopicTags(visibleTopicTags);
  const featuredLayoutPosts = getFeaturedLayoutPosts(featuredPosts, latestInitialPosts);
  const hasFeaturedLayout = featuredLayoutPosts.length >= FEATURED_POST_COUNT;
  const editorialPostIds = new Set(
    hasFeaturedLayout ? featuredLayoutPosts.map((post) => post.id) : [],
  );
  const latestPosts = getLatestPosts(latestInitialPosts);
  const topicSections = getTopicSections(popularTopicTags, latestInitialPosts, editorialPostIds);

  return (
    <PageContent className={'flex-1'} contentClassName={'flex-1'} nativeSafeTop={true}>
      <View className={'flex-1'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          <H1
            title={t('blog.index_posts.title')}
            subtitle={t('blog.index_posts.description')}
            center={true}
            hideTitleWhenNativeHeader={true}
          />

          {hasFeaturedLayout ? (
            <View className={'md:mt-2'}>
              <FeaturedPosts
                posts={featuredLayoutPosts}
                tags={popularTopicTags}
                sidebarPosts={latestPosts}
              />
            </View>
          ) : (
            <BlogTopicsPanel
              tags={popularTopicTags}
              posts={latestPosts}
              className={'mx-auto mb-6 w-full max-w-xl md:mb-8'}
            />
          )}

          <PopularTopicSections sections={topicSections} />
        </View>
      </View>
    </PageContent>
  );
};

function BlogTopicsPanel({
  tags = [],
  posts = [],
  className = '',
}: {
  tags?: Tag[];
  posts?: Post[];
  className?: string;
}) {
  const { t } = useTranslation('screens');
  const route = useRoute();
  const visibleTags = tags.filter((tag) => tag.slug && tag.name).slice(0, POPULAR_TOPIC_COUNT);
  const visiblePosts = posts.slice(0, LATEST_POST_COUNT);

  return (
    <View className={className}>
      {visibleTags.length > 0 && (
        <View>
          <H2 title={t('blog.index_posts.popular_topics')} marginTop={false} marginBottom={false} />

          <View className={'mt-3 md:mt-4'}>
            {visibleTags.map((tag) => (
              <Link key={tag.id} href={route('blog.tags.show', { tag: tag.slug })} asBlock>
                <View className={'py-1.5 md:py-2'}>
                  <View className={'flex-row items-baseline justify-between gap-3'}>
                    <Text className={'text-brand-brown-dark/85 flex-1 group-hover:underline'}>
                      {tag.name}
                    </Text>

                    {typeof tag.posts_count === 'number' && (
                      <Text className={'text-brand-brown-dark/55 shrink-0 text-sm'}>
                        {tag.posts_count}
                      </Text>
                    )}
                  </View>
                </View>
              </Link>
            ))}
          </View>
        </View>
      )}

      {visiblePosts.length > 0 && (
        <View className={'mt-7 md:mt-10'}>
          <H2 title={t('blog.index_posts.latest_posts')} marginTop={false} marginBottom={false} />

          <View className={'mt-3 md:mt-4'}>
            {visiblePosts.map((post) => (
              <Link key={post.id} href={route('blog.show', { post: post.slug })} asBlock>
                <View className={'border-brand-brown-dark/10 border-b py-3 md:py-4'}>
                  <Text className={'text-brand-brown-dark font-bold group-hover:underline'}>
                    {post.title}
                  </Text>

                  <PostMeta post={post} compact={true} className={'mt-1'} />
                </View>
              </Link>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

function PopularTopicSections({
  sections,
}: {
  sections: {
    tag: Tag;
    posts: Post[];
  }[];
}) {
  const { t } = useTranslation('screens');
  const route = useRoute();

  if (sections.length === 0) {
    return null;
  }

  return (
    <View className={'mt-10 md:mt-16 lg:mt-20'}>
      <View className={'gap-10 md:gap-16'}>
        {sections.map((section) => (
          <View key={section.tag.id}>
            <H2
              title={section.tag.name}
              linkTitle={t('blog.index_posts.view_all')}
              linkHref={route('blog.tags.show', { tag: section.tag.slug })}
              marginTop={false}
              marginBottom={true}
            />

            <View
              className={
                platformOS === 'web'
                  ? 'flex-cols-1 md:flex-cols-3 flex-row flex-wrap gap-6 md:gap-8'
                  : 'gap-6'
              }
            >
              {section.posts.map((post) => (
                <View key={post.id}>
                  <PostCard post={post} />
                </View>
              ))}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

function FeaturedPosts({
  posts,
  tags,
  sidebarPosts,
}: {
  posts: Post[];
  tags: Tag[];
  sidebarPosts: Post[];
}) {
  const { t } = useTranslation('screens');
  const mainPost = posts[0];

  if (!mainPost || posts.length < FEATURED_POST_COUNT) {
    return null;
  }

  return (
    <View className={'gap-8 lg:flex-row lg:items-start lg:gap-12 xl:gap-16'}>
      <View className={'min-w-0 lg:flex-1'}>
        <H2 title={t('blog.index_posts.featured_posts')} marginTop={false} />

        <View className={'gap-6 md:gap-8'}>
          <FeaturedPostCard post={mainPost} variant={'large'} />

          <View className={'grid gap-5 md:grid-cols-3 md:gap-6'}>
            {posts.slice(1, FEATURED_POST_COUNT).map((post) => (
              <FeaturedPostCard key={post.id} post={post} variant={'compact'} mobileHorizontal />
            ))}
          </View>
        </View>
      </View>

      <BlogTopicsPanel
        tags={tags}
        posts={sidebarPosts}
        className={'w-full lg:w-80 lg:shrink-0 xl:w-96'}
      />
    </View>
  );
}

function FeaturedPostCard({
  post,
  variant,
  mobileHorizontal = false,
}: {
  post: Post;
  variant: 'large' | 'horizontal' | 'compact';
  mobileHorizontal?: boolean;
}) {
  const route = useRoute();
  const large = variant === 'large';
  const horizontal = variant === 'horizontal';

  return (
    <Link
      href={route('blog.show', { post: post.slug })}
      asBlock={platformOS !== 'web'}
      asInline={platformOS === 'web'}
      containerClassName={
        large
          ? 'flex flex-col lg:flex-1 lg:flex-row'
          : horizontal
            ? 'flex flex-col md:flex-row lg:flex-1'
            : mobileHorizontal
              ? 'flex flex-row gap-3 md:flex-1 md:flex-col md:gap-0'
              : 'flex flex-col md:flex-1'
      }
    >
      <Image
        src={getPostImage(post)}
        alt={post.title}
        containerClassName={
          large
            ? 'aspect-4/3 w-full lg:w-5/12'
            : horizontal
              ? 'aspect-video w-full md:w-1/2'
              : mobileHorizontal
                ? 'aspect-video w-32 shrink-0 sm:w-40 md:w-full'
                : 'aspect-video w-full'
        }
        imageClassName={'rounded object-cover group-hover:opacity-80'}
        priority={large}
        sizeType={large ? 'large' : 'card'}
      />

      <View
        className={
          large
            ? 'pt-3 lg:flex-1 lg:pl-6 lg:pt-0'
            : horizontal
              ? 'pt-3 md:flex-1 md:pl-4 md:pt-0'
              : mobileHorizontal
                ? 'min-w-0 flex-1 md:pt-3'
                : 'pt-3'
        }
      >
        <PostTopicText post={post} />

        <Text
          className={
            large
              ? 'text-brand-brown-dark mt-1 text-2xl font-bold leading-tight group-hover:underline md:text-3xl lg:text-4xl'
              : 'text-brand-brown-dark mt-1 text-base font-bold leading-tight group-hover:underline md:text-lg'
          }
          numberOfLines={large ? undefined : 2}
        >
          {post.title}
        </Text>

        {(large || horizontal) && (
          <Text
            className={`text-brand-brown mt-2 text-sm leading-relaxed md:text-base ${large ? 'lg:text-lg' : ''}`}
            numberOfLines={large ? 3 : 3}
          >
            {post.summary}
          </Text>
        )}

        <PostMeta post={post} className={'mt-3'} />
      </View>
    </Link>
  );
}

function PostTopicText({ post }: { post: Post }) {
  const tag = post.tags?.[0];

  if (!tag) {
    return null;
  }

  return (
    <Text className={'text-brand-brown-dark/60 text-xs font-bold uppercase md:text-sm'}>
      {tag.name}
    </Text>
  );
}

function PostMeta({
  post,
  compact = false,
  className = '',
}: {
  post: Post;
  compact?: boolean;
  className?: string;
}) {
  const { t: tComponents } = useTranslation('components');
  const { formatDateCustom } = useFormatter();

  if (!post.published_at) {
    return null;
  }

  if (compact) {
    return (
      <Text className={`text-brand-brown-dark/60 shrink-0 text-sm ${className}`}>
        {formatDateCustom(post.published_at, 'do LLLL yyyy')}
        {' · '}
        {tComponents('post_card.reading_time', { count: post.reading_time })}
      </Text>
    );
  }

  return (
    <Text className={`text-brand-brown-dark/60 text-sm ${className}`}>
      {formatDateCustom(post.published_at, 'do LLLL yyyy')}
      {' · '}
      {tComponents('post_card.reading_time', { count: post.reading_time })}
    </Text>
  );
}

function getPopularTopicTags(tags: Tag[]) {
  const availableTags = tags.filter((tag) => tag.slug && tag.name);
  const hasFeaturedOrder = availableTags.some((tag) => typeof tag.featured_order === 'number');
  const hasPostCounts = availableTags.some((tag) => typeof tag.posts_count === 'number');

  if (hasFeaturedOrder) {
    return [...availableTags]
      .sort(
        (a, b) =>
          (a.featured_order ?? Number.MAX_SAFE_INTEGER) -
          (b.featured_order ?? Number.MAX_SAFE_INTEGER),
      )
      .slice(0, POPULAR_TOPIC_COUNT);
  }

  if (!hasPostCounts) {
    return availableTags.slice(0, POPULAR_TOPIC_COUNT);
  }

  const originalOrder = new Map(availableTags.map((tag, index) => [tag.id, index]));

  return [...availableTags]
    .sort((a, b) => {
      const countDifference = (b.posts_count ?? 0) - (a.posts_count ?? 0);

      if (countDifference !== 0) {
        return countDifference;
      }

      return (originalOrder.get(a.id) ?? 0) - (originalOrder.get(b.id) ?? 0);
    })
    .slice(0, POPULAR_TOPIC_COUNT);
}

function getTopicSections(tags: Tag[], posts: Post[], excludedPostIds: Set<number>) {
  const usedPostIds = new Set(excludedPostIds);

  return tags.flatMap((tag) => {
    const hasPostsFromTag = tag.posts?.length > 0;
    const postsForTag = hasPostsFromTag
      ? tag.posts
      : posts.filter((post) => post.tags?.some((postTag) => postTag.id === tag.id));
    const matchingPosts = getPostsByLatestPublishedDate(
      postsForTag.filter(
        (post) =>
          !usedPostIds.has(post.id) &&
          (hasPostsFromTag || post.tags?.some((postTag) => postTag.id === tag.id)),
      ),
    ).slice(0, 3);

    if (matchingPosts.length === 0) {
      return [];
    }

    matchingPosts.forEach((post) => usedPostIds.add(post.id));

    return [{ tag, posts: matchingPosts }];
  });
}

function mergeFeaturedTags(featuredTags: Tag[], fallbackTags: Tag[]) {
  const fallbackById = new Map(fallbackTags.map((tag) => [tag.id, tag]));

  return featuredTags.map((tag) => {
    const fallbackTag = fallbackById.get(tag.id);

    return {
      ...fallbackTag,
      ...tag,
      posts: tag.posts?.length > 0 ? tag.posts : (fallbackTag?.posts ?? []),
      posts_count: tag.posts_count ?? fallbackTag?.posts_count,
    };
  });
}

function getFeaturedLayoutPosts(featuredPosts: Post[], latestPosts: Post[]) {
  const postsById = new Map<number, Post>();

  [...featuredPosts, ...latestPosts].forEach((post) => {
    if (postsById.size >= FEATURED_POST_COUNT) {
      return;
    }

    if (!postsById.has(post.id)) {
      postsById.set(post.id, post);
    }
  });

  return Array.from(postsById.values());
}

function getLatestPosts(posts: Post[]) {
  return getPostsByLatestPublishedDate(posts).slice(0, LATEST_POST_COUNT);
}

function getPostsByLatestPublishedDate(posts: Post[]) {
  return [...posts].sort(comparePostsByLatestPublishedDate);
}

function comparePostsByLatestPublishedDate(a: Post, b: Post) {
  const publishedAtDifference = getPostPublishedTime(b) - getPostPublishedTime(a);

  if (publishedAtDifference !== 0) {
    return publishedAtDifference;
  }

  return b.id - a.id;
}

function getPostPublishedTime(post: Post) {
  return post.published_at ? Date.parse(post.published_at) || 0 : 0;
}

function getTagsFromPosts(posts: Post[]) {
  const tags = new Map<number, Tag>();

  posts.forEach((post) => {
    (post.tags ?? []).forEach((tag) => {
      const existing = tags.get(tag.id);

      tags.set(tag.id, {
        ...existing,
        ...tag,
        posts_count: tag.posts_count ?? (existing?.posts_count ?? 0) + 1,
      });
    });
  });

  return Array.from(tags.values());
}

function getFeaturedBlogPostsRoute() {
  const blogIndexPath = getInternalRoutePath('blog.index');

  if (!blogIndexPath) {
    throw new Error('Missing internal route for blog.index');
  }

  return {
    url: `${blogIndexPath}/features`,
    method: 'get' as const,
  };
}

IndexPostsScreen.pageBuilder = new PageBuilder<IndexPostsScreenProps, Record<string, never>>()
  .request(({ req }) =>
    req<{ posts: Paginator<Post> }>(apiRoute('posts.index'), {
      per_page: BLOG_INDEX_POSTS_PER_PAGE,
      sort_by: LATEST_POST_SORT,
    }).then((data) => ({
      initialPosts: compactPostCardPaginator(data.posts),
    })),
  )
  .request(({ req }) =>
    req<{ featured_posts: Post[]; featured_tags: Tag[] }>(getFeaturedBlogPostsRoute()).then(
      (data) => ({
        featuredPosts: compactPostCardList(data.featured_posts),
        featuredTags: compactTagsForLinks(data.featured_tags),
      }),
    ),
  )
  .request(({ req }) =>
    req<{ tags: Tag[] }>(apiRoute('tags.index'), { type: TagType.Blog }).then((data) => ({
      topicTags: compactTagsForBlogTopics(data.tags),
    })),
  );

export { IndexPostsScreen };
export type { IndexPostsScreenProps };
